import { Source, Destination, Connection, SIGNAL_TYPES, CONNECTION_STATUS } from '@mediaconnect-router/shared'

/**
 * Generate mock sources for development and testing
 */
export function generateMockSources(count: number = 10): Source[] {
  const sources: Source[] = []
  
  for (let i = 0; i < count; i++) {
    const sourceTypes = Object.values(SIGNAL_TYPES)
    const statuses: ('active' | 'inactive' | 'error')[] = ['active', 'inactive', 'error']
    
    sources.push({
      id: `source-${i + 1}`,
      name: `Source ${i + 1}`,
      type: sourceTypes[i % sourceTypes.length] as 'video' | 'audio' | 'data',
      status: statuses[i % statuses.length] as 'active' | 'inactive' | 'error',
      metadata: {
        description: `Mock source ${i + 1} for testing`,
        location: `Rack ${Math.floor(i / 4) + 1}`,
        port: `Port ${(i % 4) + 1}`
      }
    })
  }
  
  return sources
}

/**
 * Generate mock destinations for development and testing
 */
export function generateMockDestinations(count: number = 8): Destination[] {
  const destinations: Destination[] = []
  
  for (let i = 0; i < count; i++) {
    const destinationTypes = Object.values(SIGNAL_TYPES)
    const statuses: ('active' | 'inactive' | 'error')[] = ['active', 'inactive', 'error']
    
    destinations.push({
      id: `dest-${i + 1}`,
      name: `Destination ${i + 1}`,
      type: destinationTypes[i % destinationTypes.length] as 'video' | 'audio' | 'data',
      status: statuses[i % statuses.length] as 'active' | 'inactive' | 'error',
      metadata: {
        description: `Mock destination ${i + 1} for testing`,
        location: `Output Rack ${Math.floor(i / 4) + 1}`,
        port: `Output ${(i % 4) + 1}`
      }
    })
  }
  
  return destinations
}

/**
 * Generate mock connections for development and testing
 */
export function generateMockConnections(
  sources: Source[], 
  destinations: Destination[], 
  connectionRatio: number = 0.3
): Connection[] {
  const connections: Connection[] = []
  const connectionStatuses = Object.values(CONNECTION_STATUS)
  
  // Create some random connections based on the ratio
  const totalPossibleConnections = sources.length * destinations.length
  const numberOfConnections = Math.floor(totalPossibleConnections * connectionRatio)
  
  const usedPairs = new Set<string>()
  
  for (let i = 0; i < numberOfConnections; i++) {
    let sourceIndex: number
    let destinationIndex: number
    let pairKey: string
    
    // Ensure unique source-destination pairs
    do {
      sourceIndex = Math.floor(Math.random() * sources.length)
      destinationIndex = Math.floor(Math.random() * destinations.length)
      pairKey = `${sources[sourceIndex]?.id}-${destinations[destinationIndex]?.id}`
    } while (usedPairs.has(pairKey))
    
    usedPairs.add(pairKey)
    
    const source = sources[sourceIndex]
    const destination = destinations[destinationIndex]
    
    if (source && destination) {
      connections.push({
        sourceId: source.id,
        destinationId: destination.id,
        status: connectionStatuses[i % connectionStatuses.length] as any,
        timestamp: new Date(Date.now() - Math.random() * 86400000), // Random time within last 24 hours
        metadata: {
          establishedBy: 'mock-user',
          priority: Math.floor(Math.random() * 10) + 1,
          bandwidth: `${Math.floor(Math.random() * 1000) + 100}Mbps`
        }
      })
    }
  }
  
  return connections
}

/**
 * Generate a complete mock routing scenario
 */
export function generateMockRoutingScenario(options?: {
  sourceCount?: number
  destinationCount?: number
  connectionRatio?: number
}): {
  sources: Source[]
  destinations: Destination[]
  connections: Connection[]
} {
  const {
    sourceCount = 12,
    destinationCount = 8,
    connectionRatio = 0.25
  } = options || {}
  
  const sources = generateMockSources(sourceCount)
  const destinations = generateMockDestinations(destinationCount)
  const connections = generateMockConnections(sources, destinations, connectionRatio)
  
  return { sources, destinations, connections }
}

/**
 * Generate mock data for specific broadcast scenarios
 */
export const mockScenarios = {
  /**
   * Small studio setup with basic routing
   */
  smallStudio: () => generateMockRoutingScenario({
    sourceCount: 6,
    destinationCount: 4,
    connectionRatio: 0.4
  }),
  
  /**
   * Medium broadcast facility
   */
  mediumFacility: () => generateMockRoutingScenario({
    sourceCount: 24,
    destinationCount: 16,
    connectionRatio: 0.3
  }),
  
  /**
   * Large broadcast center with many sources and destinations
   */
  largeBroadcastCenter: () => generateMockRoutingScenario({
    sourceCount: 64,
    destinationCount: 48,
    connectionRatio: 0.2
  }),
  
  /**
   * Stress test scenario with maximum expected load
   */
  stressTest: () => generateMockRoutingScenario({
    sourceCount: 100,
    destinationCount: 100,
    connectionRatio: 0.1
  }),
  
  /**
   * Empty routing matrix for testing initial states
   */
  empty: () => ({
    sources: [],
    destinations: [],
    connections: []
  }),
  
  /**
   * Single connection for minimal testing
   */
  minimal: () => generateMockRoutingScenario({
    sourceCount: 1,
    destinationCount: 1,
    connectionRatio: 1.0
  })
}

/**
 * Generate mock WebSocket messages for testing real-time updates
 */
export function generateMockWebSocketMessages(scenario: ReturnType<typeof generateMockRoutingScenario>) {
  return {
    routingUpdate: {
      type: 'routing_update' as const,
      connections: scenario.connections,
      timestamp: new Date()
    },
    
    connectionRequest: (sourceId: string, destinationId: string, action: 'connect' | 'disconnect') => ({
      type: 'connection_request' as const,
      sourceId,
      destinationId,
      action,
      requestId: `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
    }),
    
    connectionResponse: (requestId: string, success: boolean, connection?: Connection, error?: string) => ({
      type: 'connection_response' as const,
      requestId,
      success,
      connection,
      error
    })
  }
}